import { mockOrderingProviders } from '@ldt/api/mock_ordering_providers';
import {
  LdtOrderDetailView,
  LdtOrderRepository,
} from '@ldt/api/order_repository';
import type { LdtOrderUpdateOptions } from '@ldt/types/order';
import { FakeOrderRepository } from 'src/api/fake_order_repository';
import { OrdersQuery, OrdersQueryVariables } from 'src/api/order_repository';
import {
  ArteraFaxStatus,
  BlockId,
  CreateOrderingProviderInput,
  FaxFieldsFragment,
  FaxType,
  LabType,
  LdtOrderDetailDirectUploadLabTechFieldsFragment,
  LdtOrderDetailFieldsFragment,
  LdtOrderForReviseReportFieldsFragment,
  NccnRiskCategory,
  OrderingPathway,
  OrderingProviderFieldsFragment,
  OrderStatus,
  PathologyFaxAction,
  PathologyLabFieldsFragment,
  PathologyLabType,
  PatientStatusAtBiopsy,
  PrimaryDiagnosisCode,
  ReviseReportInput,
  Seq,
  SortBy,
  SpecimenType,
  SurgicalMargin,
  TStage,
  UpdateOrderingProviderInput,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { CursorPaginatedResponse } from 'src/types/pagination';
import { WorkflowType } from 'src/types/workflows';
import { isOrderingPhysician } from 'src/utils';

/** Mock pathology labs for getPathologyLabs and updateOrderPathologyLab. */
const FAKE_PATHOLOGY_LABS = [
  {
    id: 'path-lab-1',
    name: 'Massachusetts General Hospital Pathology',
    phoneNumber: '617-555-1000',
    faxNumber: '617-555-1001',
    email: 'pathology@mgh.harvard.edu',
    street: '55 Fruit Street',
    city: 'Boston',
    state: 'MA',
    zipCode: '02114',
    country: 'USA',
    salesforceId: 'path-lab-1',
    type: PathologyLabType.Opd,
  },
  {
    id: 'path-lab-2',
    name: "Brigham and Women's Hospital Pathology",
    phoneNumber: '617-555-2000',
    faxNumber: '617-555-2001',
    email: 'pathology@bwh.harvard.edu',
    street: '75 Francis Street',
    city: 'Boston',
    state: 'MA',
    zipCode: '02115',
    country: 'USA',
    salesforceId: 'path-lab-2',
    type: PathologyLabType.Opd,
  },
  {
    id: 'path-lab-3',
    name: 'Beth Israel Deaconess Medical Center Pathology',
    phoneNumber: '617-555-3000',
    faxNumber: '617-555-3001',
    email: 'pathology@bidmc.harvard.edu',
    street: '330 Brookline Avenue',
    city: 'Boston',
    state: 'MA',
    zipCode: '02215',
    country: 'USA',
    salesforceId: 'path-lab-3',
    type: PathologyLabType.Opd,
  },
  {
    id: 'path-lab-4',
    name: 'Direct Upload Lab',
    phoneNumber: '617-555-4000',
    faxNumber: '617-555-4001',
    email: 'pathology@directuploadlab.com',
    street: '123 Main Street',
    city: 'Boston',
    state: 'MA',
    zipCode: '02215',
    country: 'USA',
    salesforceId: 'path-lab-4',
    type: PathologyLabType.DirectUpload,
  },
] as unknown as Array<PathologyLabFieldsFragment & { salesforceId: string }>;

// IDs must be base64-encoded "Order:N" so they pass order_loaders validateOrderIdParam
const prostateOrder1 = {
  primaryGleasonScore: 3,
  secondaryGleasonScore: 3,
  psa: 1.0,
  patientAge: 65,
  tStage: TStage.T1C,
  nccnRiskCategory: NccnRiskCategory.Low,
  id: 'T3JkZXI6MQ==', // btoa('Order:1')
  patient: { id: 'p_patient_id_1' },
  crossOrgId: 'cross_org_id_1',
  status: OrderStatus.Draft,
  specimenType: SpecimenType.ProstateBiopsy,
  labType: LabType.ArteraLab,
  createdAt: '2023-10-01T12:00:00Z',
  updatedAt: '2023-10-01T12:00:00Z',
  statusUpdatedAt: '2023-10-01T12:00:00Z',
} as unknown as LdtOrderDetailFieldsFragment;

const prostateOrder2 = {
  primaryGleasonScore: 4,
  secondaryGleasonScore: 3,
  psa: 5.2,
  patientAge: 72,
  tStage: TStage.T2A,
  nccnRiskCategory: NccnRiskCategory.IntermediateFavorable,
  id: 'T3JkZXI6Mg==', // btoa('Order:2')
  patient: {
    id: 'p_patient_id_2',
    firstNamePhi: 'Robert',
    lastNamePhi: 'Williams',
    dateOfBirthPhi: '1951-03-15',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_2',
  status: OrderStatus.QualityControlPending,
  specimenType: SpecimenType.ProstateBiopsy,
  labType: LabType.DirectUploadLab,
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  imageQcRejectionReason: 'Incorrect patient',
  specimenId: 'specimen_id_2',
  bxlinkAccession: '1234567890',
  labProcedureDatePhi: '2023-10-10',
  patientLabAccession: 'ACC-ORDER-002',
  slideSamplePart: 'Part B',
  orderingProvider: {
    id: 'provider-1',
    siteName: 'Boston Medical Center',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '617-555-0101',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'John',
      lastName: 'Smith',
      npi: '1234567890',
      email: 'john.smith@bostonmedical.com',
    },
  },
  pathologyLab: {
    id: 'path-lab-1',
    name: 'Massachusetts General Hospital Pathology',
    phoneNumber: '617-555-1000',
    faxNumber: '617-555-1001',
    email: 'pathology@mgh.harvard.edu',
    street: '55 Fruit Street',
    city: 'Boston',
    state: 'MA',
    zipCode: '02114',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-1',
          filename: 'slide_image_001.tif',
          category: UploadedFileCategory.SlideImage,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2023-10-15T10:00:00Z',
          updatedAt: '2023-10-15T10:05:00Z',
        },
      },
      {
        node: {
          id: 'file-2',
          filename: 'test_requisition_form.pdf',
          category: UploadedFileCategory.TestRequisitionForm,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2023-10-15T10:10:00Z',
          updatedAt: '2023-10-15T10:10:00Z',
        },
      },
      {
        node: {
          id: 'file-3',
          filename: 'pathology_report.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2023-10-15T10:10:00Z',
          updatedAt: '2023-10-15T10:10:00Z',
        },
      },
      {
        node: {
          id: 'file-4',
          filename: 'face_sheet.pdf',
          category: UploadedFileCategory.FaceSheet,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2023-10-15T10:10:00Z',
          updatedAt: '2023-10-15T10:10:00Z',
        },
      },
      {
        node: {
          id: 'file-5',
          filename: 'medical_note.pdf',
          category: UploadedFileCategory.MedicalNote,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2023-10-15T10:10:00Z',
          updatedAt: '2023-10-15T10:10:00Z',
        },
      },
      {
        node: {
          id: 'file-6',
          filename: 'face_sheet_2.pdf',
          category: UploadedFileCategory.FaceSheet,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2023-10-15T10:10:00Z',
          updatedAt: '2023-10-15T10:10:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  patientStatusAtBiopsy: PatientStatusAtBiopsy.NonHospitalOutpatient,
  patientDischargeDate: '2025-02-20',
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  totalCores: 12,
  positiveCores: 3,
  hasBillingHold: false,
  hasAiReport: true,
  createdByCs: false,
  createdAt: '2023-10-15T09:00:00Z',
  updatedAt: '2023-10-15T10:15:00Z',
  statusUpdatedAt: '2023-10-15T10:15:00Z',
  submittedAt: '2023-10-15T10:15:00Z',
  organizationName: 'Northwestern',
} as unknown as LdtOrderDetailFieldsFragment;

const prostateOrder3 = {
  primaryGleasonScore: 4,
  secondaryGleasonScore: 4,
  psa: 8.5,
  patientAge: 68,
  tStage: TStage.T2B,
  nccnRiskCategory: NccnRiskCategory.IntermediateUnfavorable,
  totalCores: 14,
  positiveCores: 6,
  id: 'T3JkZXI6Mw==', // btoa('Order:3')
  patient: {
    id: 'p_patient_id_3',
    firstNamePhi: 'James',
    lastNamePhi: 'Johnson',
    dateOfBirthPhi: '1956-07-22',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_3',
  status: OrderStatus.ReportReady,
  specimenType: SpecimenType.ProstateBiopsy,
  labType: LabType.ArteraLab,
  specimenId: 'specimen_id_3',
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  orderingProvider: {
    id: 'provider-1',
    siteName: 'Boston Medical Center',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '617-555-0101',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'John',
      lastName: 'Smith',
      npi: '1234567890',
      email: 'john.smith@bostonmedical.com',
    },
  },
  pathologyLab: {
    id: 'path-lab-1',
    name: 'Massachusetts General Hospital Pathology',
    phoneNumber: '617-555-1000',
    faxNumber: '617-555-1001',
    email: 'pathology@mgh.harvard.edu',
    street: '55 Fruit Street',
    city: 'Boston',
    state: 'MA',
    zipCode: '02114',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-10',
          filename: 'slide_image_003.tif',
          category: UploadedFileCategory.SlideImage,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-03-01T09:00:00Z',
          updatedAt: '2024-03-01T09:05:00Z',
        },
      },
      {
        node: {
          id: 'file-11',
          filename: 'pathology_report_003.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-03-01T09:10:00Z',
          updatedAt: '2024-03-01T09:10:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  patientStatusAtBiopsy: PatientStatusAtBiopsy.NonHospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: true,
  createdByCs: false,
  createdAt: '2024-03-01T08:00:00Z',
  updatedAt: '2024-03-01T10:00:00Z',
  statusUpdatedAt: '2024-03-01T10:00:00Z',
  submittedAt: '2024-03-01T08:30:00Z',
  organizationName: 'Northwestern',
} as unknown as LdtOrderDetailFieldsFragment;

const metastaticClinicOrder4 = {
  psa: 45.2,
  patientAge: 71,
  tStage: TStage.T3A,
  id: 'T3JkZXI6NA==', // btoa('Order:4')
  patient: {
    id: 'p_patient_id_4',
    firstNamePhi: 'Michael',
    lastNamePhi: 'Davis',
    dateOfBirthPhi: '1953-11-08',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_4',
  status: OrderStatus.ReportReady,
  specimenType: SpecimenType.MetastaticProstateBiopsy,
  labType: LabType.ArteraLab,
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  orderingProvider: {
    id: 'provider-2',
    siteName: 'Johns Hopkins Hospital',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '410-555-0201',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'Sarah',
      lastName: 'Chen',
      npi: '9876543210',
      email: 'sarah.chen@hopkinsmedicine.org',
    },
  },
  pathologyLab: {
    id: 'path-lab-2',
    name: "Brigham and Women's Hospital Pathology",
    phoneNumber: '617-555-2000',
    faxNumber: '617-555-2001',
    email: 'pathology@bwh.harvard.edu',
    street: '75 Francis Street',
    city: 'Boston',
    state: 'MA',
    zipCode: '02115',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-20',
          filename: 'metastatic_slide_image.tif',
          category: UploadedFileCategory.SlideImage,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-04-10T09:00:00Z',
          updatedAt: '2024-04-10T09:05:00Z',
        },
      },
      {
        node: {
          id: 'file-21',
          filename: 'metastatic_pathology_report.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-04-10T09:10:00Z',
          updatedAt: '2024-04-10T09:10:00Z',
        },
      },
      {
        node: {
          id: 'file-22',
          filename: 'consult_report.pdf',
          category: UploadedFileCategory.ConsultReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-04-10T09:15:00Z',
          updatedAt: '2024-04-10T09:15:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  specimenId: 'specimen_id_4',
  patientStatusAtBiopsy: PatientStatusAtBiopsy.NonHospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: true,
  createdByCs: false,
  createdAt: '2024-04-10T08:00:00Z',
  updatedAt: '2024-04-10T11:00:00Z',
  statusUpdatedAt: '2024-04-10T11:00:00Z',
  submittedAt: '2024-04-10T08:30:00Z',
  organizationName: 'Northwestern',
} as unknown as LdtOrderDetailFieldsFragment;

/** Tempus metastatic prostate biopsy — QC Approved, has AI report. */
const metastaticTempusOrder5 = {
  psa: 18.3,
  tStage: TStage.T3A,
  labProcedureDatePhi: '2024-05-12',
  id: 'T3JkZXI6NQ==', // btoa('Order:5')
  patient: {
    id: 'p_patient_id_5',
    firstNamePhi: 'Thomas',
    lastNamePhi: 'Rivera',
    dateOfBirthPhi: '1958-02-20',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_5',
  status: OrderStatus.QualityControlApproved,
  specimenType: SpecimenType.MetastaticProstateBiopsy,
  labType: LabType.ArteraLab,
  externalId: 'TMP-1005',
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  orderingProvider: {
    id: 'provider-3',
    siteName: 'Mayo Clinic',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '507-555-0301',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'David',
      lastName: 'Patel',
      npi: '1122334455',
      email: 'david.patel@mayoclinic.org',
    },
  },
  pathologyLab: {
    id: 'path-lab-3',
    name: 'Mayo Clinic Pathology',
    phoneNumber: '507-555-1100',
    faxNumber: '507-555-1101',
    email: 'pathology@mayoclinic.org',
    street: '200 First Street SW',
    city: 'Rochester',
    state: 'MN',
    zipCode: '55905',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-30',
          filename: 'tempus_slide_image_005.tif',
          category: UploadedFileCategory.SlideImage,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-05-12T09:00:00Z',
          updatedAt: '2024-05-12T09:05:00Z',
        },
      },
      {
        node: {
          id: 'file-31',
          filename: 'tempus_pathology_report_005.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-05-12T09:10:00Z',
          updatedAt: '2024-05-12T09:10:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  specimenId: 'specimen_id_5',
  patientStatusAtBiopsy: PatientStatusAtBiopsy.NonHospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: true,
  createdByCs: false,
  createdAt: '2024-05-12T08:00:00Z',
  updatedAt: '2024-05-12T11:30:00Z',
  statusUpdatedAt: '2024-05-12T11:30:00Z',
  submittedAt: '2024-05-12T08:45:00Z',
  organizationName: 'Tempus',
} as unknown as LdtOrderDetailFieldsFragment;

/** Tempus metastatic prostate biopsy — QC Approved, no AI report, missing clinical data. */
const metastaticTempusOrder6 = {
  psa: null,
  tStage: null,
  labProcedureDatePhi: null,
  id: 'T3JkZXI6Ng==', // btoa('Order:6')
  patient: {
    id: 'p_patient_id_6',
    firstNamePhi: 'Carlos',
    lastNamePhi: 'Nguyen',
    dateOfBirthPhi: '1964-09-03',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_6',
  status: OrderStatus.QualityControlApproved,
  specimenType: SpecimenType.MetastaticProstateBiopsy,
  labType: LabType.ArteraLab,
  externalId: 'TMP-1006',
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  slideSamplePart: 'Part A',
  patientLabAccession: 'ACC-ORDER-006',
  orderingProvider: {
    id: 'provider-3',
    siteName: 'Mayo Clinic',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '507-555-0301',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'David',
      lastName: 'Patel',
      npi: '1122334455',
      email: 'david.patel@mayoclinic.org',
    },
  },
  pathologyLab: {
    id: 'path-lab-3',
    name: 'Mayo Clinic Pathology',
    phoneNumber: '507-555-1100',
    faxNumber: '507-555-1101',
    email: 'pathology@mayoclinic.org',
    street: '200 First Street SW',
    city: 'Rochester',
    state: 'MN',
    zipCode: '55905',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-32',
          filename: 'tempus_pathology_report_006.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-05-20T10:00:00Z',
          updatedAt: '2024-05-20T10:00:00Z',
        },
      },
      {
        node: {
          id: 'file-33',
          filename: 'tempus_test_requisition_form_006.pdf',
          category: UploadedFileCategory.TestRequisitionForm,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-05-20T10:00:00Z',
          updatedAt: '2024-05-20T10:00:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  specimenId: 'specimen_id_6',
  patientStatusAtBiopsy: PatientStatusAtBiopsy.HospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: false,
  createdByCs: false,
  createdAt: '2024-05-20T09:00:00Z',
  updatedAt: '2024-05-20T12:00:00Z',
  statusUpdatedAt: '2024-05-20T12:00:00Z',
  submittedAt: '2024-05-20T09:30:00Z',
  organizationName: 'Tempus',
} as unknown as LdtOrderDetailFieldsFragment;

/** Tempus metastatic prostate biopsy — QC Pending, has AI report. */
const metastaticTempusOrder7 = {
  psa: 22.1,
  tStage: TStage.T2B,
  labProcedureDatePhi: '2024-06-01',
  id: 'T3JkZXI6Nw==', // btoa('Order:7')
  patient: {
    id: 'p_patient_id_7',
    firstNamePhi: 'William',
    lastNamePhi: 'Foster',
    dateOfBirthPhi: '1955-07-14',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_7',
  status: OrderStatus.QualityControlPending,
  specimenType: SpecimenType.MetastaticProstateBiopsy,
  labType: LabType.ArteraLab,
  externalId: 'TMP-1007',
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  orderingProvider: {
    id: 'provider-3',
    siteName: 'Mayo Clinic',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '507-555-0301',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'David',
      lastName: 'Patel',
      npi: '1122334455',
      email: 'david.patel@mayoclinic.org',
    },
  },
  pathologyLab: {
    id: 'path-lab-3',
    name: 'Mayo Clinic Pathology',
    phoneNumber: '507-555-1100',
    faxNumber: '507-555-1101',
    email: 'pathology@mayoclinic.org',
    street: '200 First Street SW',
    city: 'Rochester',
    state: 'MN',
    zipCode: '55905',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-33',
          filename: 'tempus_slide_image_007.tif',
          category: UploadedFileCategory.SlideImage,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-06-01T09:00:00Z',
          updatedAt: '2024-06-01T09:05:00Z',
        },
      },
      {
        node: {
          id: 'file-34',
          filename: 'tempus_pathology_report_007.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-06-01T09:10:00Z',
          updatedAt: '2024-06-01T09:10:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  specimenId: 'specimen_id_7',
  patientStatusAtBiopsy: PatientStatusAtBiopsy.NonHospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: true,
  createdByCs: false,
  createdAt: '2024-06-01T08:00:00Z',
  updatedAt: '2024-06-01T10:00:00Z',
  statusUpdatedAt: '2024-06-01T10:00:00Z',
  submittedAt: '2024-06-01T08:30:00Z',
  organizationName: 'Tempus',
} as unknown as LdtOrderDetailFieldsFragment;

/** Tempus metastatic prostate biopsy — QC Pending, no AI report. */
const metastaticTempusOrder8 = {
  psa: null,
  tStage: null,
  labProcedureDatePhi: null,
  id: 'T3JkZXI6OA==', // btoa('Order:8')
  patient: {
    id: 'p_patient_id_8',
    firstNamePhi: 'Marcus',
    lastNamePhi: 'Okafor',
    dateOfBirthPhi: '1960-11-25',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_8',
  status: OrderStatus.QualityControlPending,
  specimenType: SpecimenType.MetastaticProstateBiopsy,
  labType: LabType.ArteraLab,
  externalId: 'TMP-1008',
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  orderingProvider: {
    id: 'provider-3',
    siteName: 'Mayo Clinic',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '507-555-0301',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'David',
      lastName: 'Patel',
      npi: '1122334455',
      email: 'david.patel@mayoclinic.org',
    },
  },
  pathologyLab: {
    id: 'path-lab-3',
    name: 'Mayo Clinic Pathology',
    phoneNumber: '507-555-1100',
    faxNumber: '507-555-1101',
    email: 'pathology@mayoclinic.org',
    street: '200 First Street SW',
    city: 'Rochester',
    state: 'MN',
    zipCode: '55905',
    country: 'USA',
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-35',
          filename: 'tempus_pathology_report_008.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-06-05T10:00:00Z',
          updatedAt: '2024-06-05T10:00:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  specimenId: 'specimen_id_8',
  patientStatusAtBiopsy: PatientStatusAtBiopsy.HospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: false,
  createdByCs: false,
  createdAt: '2024-06-05T09:00:00Z',
  updatedAt: '2024-06-05T11:00:00Z',
  statusUpdatedAt: '2024-06-05T11:00:00Z',
  submittedAt: '2024-06-05T09:30:00Z',
  organizationName: 'Tempus',
} as unknown as LdtOrderDetailFieldsFragment;

/** Tempus metastatic prostate biopsy — Draft (Pathology Report Pending), no pathology report yet. */
const metastaticTempusOrder10 = {
  ...metastaticTempusOrder8,
  psa: null,
  tStage: null,
  labProcedureDatePhi: null,
  id: 'T3JkZXI6MTA=', // btoa('Order:10')
  patient: {
    ...metastaticTempusOrder8.patient,
    id: 'p_patient_id_10',
    firstNamePhi: 'Elena',
    lastNamePhi: 'Nguyen',
    dateOfBirthPhi: '1968-04-03',
  },
  crossOrgId: 'cross_org_id_10',
  status: OrderStatus.Draft,
  externalId: 'TMP-1009',
  uploadedFiles: {
    edges: [],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  specimenId: 'specimen_id_10',
  hasAiReport: false,
  createdAt: '2024-06-10T09:00:00Z',
  updatedAt: '2024-06-10T09:00:00Z',
  statusUpdatedAt: '2024-06-10T09:00:00Z',
  submittedAt: null,
} as unknown as LdtOrderDetailFieldsFragment;

const prostatePostRpOrder9 = {
  primaryGleasonScore: 4,
  secondaryGleasonScore: 4,
  psa: 8.5,
  patientAge: 68,
  tStage: TStage.T2B,
  surgicalMargin: SurgicalMargin.Positive,
  id: 'T3JkZXI6OQ==', // btoa('Order:9')
  patient: {
    id: 'p_patient_id_9',
    firstNamePhi: 'John',
    lastNamePhi: 'Doe',
    dateOfBirthPhi: '1950-01-01',
    biologicalSex: 'MALE',
  },
  crossOrgId: 'cross_org_id_9',
  status: OrderStatus.QualityControlPending,
  specimenType: SpecimenType.ProstatePostRp,
  specimenId: 'specimen_id_3',
  orderingPathway: OrderingPathway.DefaultNonReflexive,
  orderingProvider: {
    id: 'provider-1',
    siteName: 'Boston Medical Center',
    siteFaxRegionCode: 'US',
    siteFaxNumber: '617-555-0101',
    receiveReportsByFax: false,
    orderingPhysician: {
      firstName: 'John',
      lastName: 'Smith',
      npi: '1234567890',
      email: 'john.smith@bostonmedical.com',
    },
  },
  uploadedFiles: {
    edges: [
      {
        node: {
          id: 'file-36',
          filename: 'pathology_report_009.pdf',
          category: UploadedFileCategory.PathologyReport,
          status: UploadedFileStatus.Complete,
          progress: 100,
          createdAt: '2024-03-01T09:10:00Z',
          updatedAt: '2024-03-01T09:10:00Z',
        },
      },
    ],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    },
  },
  patientStatusAtBiopsy: PatientStatusAtBiopsy.NonHospitalOutpatient,
  primaryDiagnosisCodes: [PrimaryDiagnosisCode.C61],
  hasBillingHold: false,
  hasAiReport: true,
  createdByCs: false,
  createdAt: '2024-03-01T08:00:00Z',
  updatedAt: '2024-03-01T10:00:00Z',
  statusUpdatedAt: '2024-03-01T10:00:00Z',
  submittedAt: '2024-03-01T08:30:00Z',
  organizationName: 'Northwestern',
} as unknown as LdtOrderDetailFieldsFragment;

/**
 * Implementation of the Order Repository for the Ldt platform on a fake backend.
 */
export class LdtFakeOrderRepository
  extends FakeOrderRepository<LdtOrderDetailFieldsFragment>
  implements LdtOrderRepository
{
  readonly _platform = DistributionPlatform.Ldt;
  readonly _storageKey = 'ldtProstateOrders';
  readonly _initialOrders = [
    prostateOrder1,
    prostateOrder2,
    prostateOrder3,
    metastaticClinicOrder4,
    metastaticTempusOrder5,
    metastaticTempusOrder6,
    metastaticTempusOrder7,
    metastaticTempusOrder8,
    metastaticTempusOrder10,
    prostatePostRpOrder9,
  ];
  readonly _orderingProvidersStorageKey = 'ldtOrderingProviders';

  async getOrderById(
    id: string,
    options: { view: LdtOrderDetailView },
  ): Promise<
    | LdtOrderDetailFieldsFragment
    | LdtOrderDetailDirectUploadLabTechFieldsFragment
    | null
  > {
    const order = await super.getOrderById(id, options);
    if (!order) {
      return null;
    }
    if (options.view === LdtOrderDetailView.DirectUploadLabTech) {
      return {
        ...order,
        slide: {
          accession: 'FAKE-ACC-001',
          siteLabel: 'Left Apex',
          blockId: BlockId.A,
        },
      };
    }
    return order;
  }

  async getOrders(
    ordersQueryVariables: OrdersQueryVariables,
  ): Promise<OrdersQuery> {
    // First get all orders
    let allOrders = this.loadOrders();
    const page = ordersQueryVariables.page;
    const perPage = ordersQueryVariables.perPage;
    const sortBy = ordersQueryVariables.sortBy || SortBy.SubmittedAt;
    const seq = ordersQueryVariables.seq || Seq.Desc;
    const searchText = ordersQueryVariables.searchText || '';
    const statuses = ordersQueryVariables.statuses;

    // Filter orders for ordering physician users
    // They should only see orders that:
    // 1. Have an ordering provider that matches their email
    // 2. Are created by them
    if (isOrderingPhysician()) {
      const userEmail = this.getUserInfo().email.toLowerCase();
      allOrders = allOrders.filter((order) => {
        const createdByUser = order.creatorAuth0Id === this.getUserInfo().id;
        // Show orders where the ordering provider's email matches the user's email
        const providerEmail =
          order.orderingProvider?.orderingPhysician?.email?.toLowerCase();
        return providerEmail === userEmail || createdByUser;
      });
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      allOrders = allOrders.filter(
        (order) =>
          order.externalId?.toString().toLowerCase().includes(searchLower) ||
          order.id?.toLowerCase().includes(searchLower) ||
          order.crossOrgId?.toLowerCase().includes(searchLower),
      );
    }

    // Apply status filter
    if (statuses && statuses.length > 0) {
      allOrders = allOrders.filter((order) => statuses.includes(order.status));
    }

    if (ordersQueryVariables.specimenTypes?.length) {
      allOrders = allOrders.filter((order) =>
        ordersQueryVariables.specimenTypes!.includes(order.specimenType),
      );
    }

    // When Needs Attention is checked (clinic workflow only), only show orders
    // that need attention. Backend applies the full eligibility rules; this stub
    // mirrors the filter for local/fake use.
    const needsAttentionFirst =
      this.getWorkflowType() === WorkflowType.CLINIC &&
      'needsAttentionFirst' in ordersQueryVariables &&
      ordersQueryVariables.needsAttentionFirst === true;

    if (needsAttentionFirst) {
      allOrders = allOrders.filter((order) => this.orderNeedsAttention(order));
    }

    // Helper functions for sorting (duplicated from base class since they're private)
    const compareDates = (
      dateA?: Date | string | null,
      dateB?: Date | string | null,
    ): number => {
      if (!dateA && !dateB) return 0;
      if (!dateA) return -1;
      if (!dateB) return 1;
      const dateObjA = typeof dateA === 'string' ? new Date(dateA) : dateA;
      const dateObjB = typeof dateB === 'string' ? new Date(dateB) : dateB;
      return dateObjA.getTime() - dateObjB.getTime();
    };

    const compareStatuses = (
      statusA?: OrderStatus | null,
      statusB?: OrderStatus | null,
    ): number => {
      if (!statusA && !statusB) return 0;
      if (!statusA) return -1;
      if (!statusB) return 1;
      const statusPriority: Record<OrderStatus, number> = {
        [OrderStatus.Draft]: 1,
        [OrderStatus.LabProcessing]: 2,
        [OrderStatus.AiProcessing]: 3,
        [OrderStatus.AiError]: 4,
        [OrderStatus.ReportPending]: 5,
        [OrderStatus.ReportRejected]: 6,
        [OrderStatus.ReportReady]: 7,
        [OrderStatus.Canceled]: 8,
        [OrderStatus.Deleted]: 0,
        [OrderStatus.LabError]: 0,
        [OrderStatus.PathologyFaxFailed]: 0,
        [OrderStatus.PathologyFaxPending]: 0,
        [OrderStatus.QualityControlApproved]: 0,
        [OrderStatus.QualityControlPending]: 0,
        [OrderStatus.ReportFaxed]: 0,
        [OrderStatus.ReportFaxFailed]: 0,
        [OrderStatus.ReportFaxPending]: 0,
        [OrderStatus.ReportSigned]: 0,
        [OrderStatus.SlideImageProcessing]: 0,
        [OrderStatus.SlideImageQualityControlFailed]: 0,
        [OrderStatus.SlideImageQualityControlPending]: 0,
      };
      return statusPriority[statusA] - statusPriority[statusB];
    };

    allOrders = [...allOrders].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case SortBy.SubmittedAt:
          comparison = compareDates(a.submittedAt, b.submittedAt);
          break;

        case SortBy.Status:
          comparison = compareStatuses(a.status, b.status);
          break;

        case SortBy.StatusUpdatedAt:
          comparison = compareDates(a.statusUpdatedAt, b.statusUpdatedAt);
          break;
      }

      return seq === Seq.Asc ? comparison : -comparison;
    });

    const totalCount = allOrders.length;
    // Convert from 1-indexed to 0-indexed page (same as real GraphQL backend)
    const pageIndex = Math.max(0, page - 1);
    const startIndex = pageIndex * perPage;
    const endIndex = Math.min(startIndex + perPage, totalCount);
    const paginatedOrders = allOrders.slice(startIndex, endIndex);

    const includeNeedsAttention =
      'includeNeedsAttention' in ordersQueryVariables &&
      ordersQueryVariables.includeNeedsAttention === true;

    return Promise.resolve({
      orders: {
        page: page,
        totalCount: totalCount,
        items: paginatedOrders.map((order) => ({
          ...order,
          ...(includeNeedsAttention
            ? { needsAttention: this.orderNeedsAttention(order) }
            : {}),
        })),
      },
    });
  }

  /**
   * Stub of the GraphQL needsAttention resolver for local/fake use.
   * Clinic Draft and QC Pending orders are treated as needing attention.
   */
  private orderNeedsAttention(order: LdtOrderDetailFieldsFragment): boolean {
    if (this.getWorkflowType() !== WorkflowType.CLINIC) {
      return false;
    }
    return (
      order.status === OrderStatus.Draft ||
      order.status === OrderStatus.QualityControlPending
    );
  }

  /**
   * Tempus metastatic prostate biopsy status transitions on update/submit.
   *
   * QC Pending CS save (matches real backend):
   *   - Clinical vars complete → Image Pending (LabProcessing); email flag ignored
   *   - Incomplete + sendTempusMissingClinicalVariablesEmail true → QC Approved
   *   - Incomplete + email false/omitted → field updates only, status unchanged
   *
   * QC Approved is still used for Tempus (lab tech clinical entry); Clinic/Ancillary
   * no longer rest in QC Approved after the QC-approve mutation.
   */
  private applyMetastaticTempusWorkflowStatusTransition(
    existingOrder: LdtOrderDetailFieldsFragment,
    merged: LdtOrderDetailFieldsFragment,
    options?: LdtOrderUpdateOptions,
  ): LdtOrderDetailFieldsFragment {
    if (this.getWorkflowType() !== WorkflowType.TEMPUS) {
      return merged;
    }

    const sendTempusEmail = options?.sendTempusMissingClinicalVariablesEmail;
    const clinicalComplete = isMetastaticTempusClinicalDataComplete(merged);

    if (
      existingOrder.status === OrderStatus.Draft &&
      merged.status === OrderStatus.QualityControlPending
    ) {
      return {
        ...merged,
        status: clinicalComplete
          ? OrderStatus.LabProcessing
          : OrderStatus.QualityControlPending,
      };
    }

    if (existingOrder.status === OrderStatus.QualityControlPending) {
      if (clinicalComplete) {
        return {
          ...merged,
          status: OrderStatus.LabProcessing,
        };
      }
      if (sendTempusEmail === true) {
        return {
          ...merged,
          status: OrderStatus.QualityControlApproved,
        };
      }
      return {
        ...merged,
        status: existingOrder.status,
      };
    }

    if (
      existingOrder.status === OrderStatus.QualityControlApproved &&
      clinicalComplete
    ) {
      return {
        ...merged,
        status: OrderStatus.LabProcessing,
      };
    }

    return merged;
  }

  submit(
    order: LdtOrderDetailFieldsFragment,
  ): Promise<LdtOrderDetailFieldsFragment> {
    order.status = OrderStatus.QualityControlPending;
    return this.update(order);
  }

  async update(
    order: LdtOrderDetailFieldsFragment,
    options?: LdtOrderUpdateOptions,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === order.id);

    if (orderIndex !== -1) {
      // Merge the existing order with the update
      const existingOrder = orders[orderIndex];
      let updatedOrder: LdtOrderDetailFieldsFragment = {
        ...existingOrder,
        ...order,
      };

      // In the fake backend, normalize orderingProvider to the full provider
      // object (not just { id }) so the UI has access to all fields (like
      // orderingPhysician) after a refresh, matching the real GraphQL backend.
      if (updatedOrder.orderingProvider?.id) {
        const allProviders = this.loadOrderingProviders();
        const fullProvider = allProviders.find(
          (p) => p.id === updatedOrder.orderingProvider!.id,
        );
        if (fullProvider) {
          updatedOrder = {
            ...updatedOrder,
            orderingProvider: fullProvider,
          };
        }
      }

      const isTempusWorkflow = this.getWorkflowType() === WorkflowType.TEMPUS;

      if (isTempusWorkflow) {
        updatedOrder = this.applyMetastaticTempusWorkflowStatusTransition(
          existingOrder,
          updatedOrder,
          options,
        );
      }

      orders[orderIndex] = updatedOrder;
      this.persistOrders(orders);
      return Promise.resolve(updatedOrder);
    }

    throw new Error('Order not found');
  }

  async updateRejectedOrder(
    order: LdtOrderDetailFieldsFragment,
    revertStatus: OrderStatus,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const updated = await this.update(order);
    return this.updateOrderStatus(updated.id, revertStatus);
  }

  private loadOrderingProviders(): OrderingProviderFieldsFragment[] {
    const providersJson = sessionStorage.getItem(
      this._orderingProvidersStorageKey,
    );

    if (providersJson) {
      return JSON.parse(providersJson);
    }

    return mockOrderingProviders;
  }

  private persistOrderingProviders(
    providers: OrderingProviderFieldsFragment[],
  ): void {
    sessionStorage.setItem(
      this._orderingProvidersStorageKey,
      JSON.stringify(providers),
    );
  }

  async getOrderingProviders(params: {
    searchText?: string;
    cursor?: string | null;
    first?: number;
  }): Promise<CursorPaginatedResponse<OrderingProviderFieldsFragment>> {
    const { searchText, cursor, first = 15 } = params;

    // Load providers from sessionStorage (or use initial mock data)
    const allProviders = this.loadOrderingProviders();

    // Filter providers based on search text
    let filteredProviders = allProviders;

    if (isOrderingPhysician()) {
      const userEmail = this.getUserInfo().email.toLowerCase();
      filteredProviders = filteredProviders.filter(
        (provider) =>
          provider.orderingPhysician?.email?.toLowerCase() === userEmail,
      );
    }

    if (searchText && searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filteredProviders = filteredProviders.filter((provider) => {
        // Search in physician name
        const physicianName = [
          provider.orderingPhysician?.firstName,
          provider.orderingPhysician?.lastName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        // Search in site name
        const siteName = provider.siteName?.toLowerCase() || '';

        // Search in NPI
        const npi = provider.orderingPhysician?.npi?.toLowerCase() || '';

        return (
          physicianName.includes(searchLower) ||
          siteName.includes(searchLower) ||
          npi.includes(searchLower)
        );
      });
    }

    // Convert cursor to start index (cursor is the index as a string)
    const startIndex = cursor ? parseInt(cursor, 10) : 0;
    const endIndex = startIndex + first;
    const paginatedProviders = filteredProviders.slice(startIndex, endIndex);

    // Generate next cursor (index of the next item, or null if no more items)
    const hasNextPage = endIndex < filteredProviders.length;
    const nextCursor = hasNextPage ? String(endIndex) : null;

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    return {
      items: paginatedProviders,
      nextCursor,
      hasNextPage,
    };
  }

  async createOrderingProvider(
    input: CreateOrderingProviderInput,
  ): Promise<OrderingProviderFieldsFragment> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    const newProvider: OrderingProviderFieldsFragment = {
      id: Math.random().toString(36).substring(7),
      orderingPhysician: {
        firstName: input.orderingPhysician.firstName ?? null,
        lastName: input.orderingPhysician.lastName ?? null,
        npi: input.orderingPhysician.npi ?? null,
        email: input.orderingPhysician.email ?? null,
      },
      receiveReportsByFax: input.receiveReportsByFax ?? false,
      siteName: input.siteName ?? null,
      siteFaxNumber: input.siteFaxNumber ?? null,
      siteFaxRegionCode: input.siteFaxRegionCode ?? null,
    };

    // Add to providers and persist to sessionStorage
    const allProviders = this.loadOrderingProviders();
    allProviders.push(newProvider);
    this.persistOrderingProviders(allProviders);

    return newProvider;
  }

  async updateOrderingProvider(
    input: UpdateOrderingProviderInput,
  ): Promise<OrderingProviderFieldsFragment> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Load providers from sessionStorage
    const allProviders = this.loadOrderingProviders();

    // Find and update the provider
    const providerIndex = allProviders.findIndex((p) => p.id === input.id);

    if (providerIndex === -1) {
      throw new Error('Ordering provider not found');
    }

    const existing = allProviders[providerIndex];
    const updatedProvider: OrderingProviderFieldsFragment = {
      ...existing,
      orderingPhysician: input.orderingPhysician
        ? {
            firstName:
              input.orderingPhysician.firstName ??
              existing.orderingPhysician?.firstName ??
              null,
            lastName:
              input.orderingPhysician.lastName ??
              existing.orderingPhysician?.lastName ??
              null,
            npi:
              input.orderingPhysician.npi ??
              existing.orderingPhysician?.npi ??
              null,
            email:
              input.orderingPhysician.email ??
              existing.orderingPhysician?.email ??
              null,
          }
        : existing.orderingPhysician,
      receiveReportsByFax:
        input.receiveReportsByFax ?? existing.receiveReportsByFax,
      siteName:
        'siteName' in input ? (input.siteName ?? null) : existing.siteName,
      siteFaxNumber: input.siteFaxNumber ?? existing.siteFaxNumber,
      siteFaxRegionCode: input.siteFaxRegionCode ?? existing.siteFaxRegionCode,
    };

    allProviders[providerIndex] = updatedProvider;
    this.persistOrderingProviders(allProviders);

    return updatedProvider;
  }

  async deleteOrderingProvider(id: string): Promise<void> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Load providers from sessionStorage
    const allProviders = this.loadOrderingProviders();

    // Find and remove the provider
    const providerIndex = allProviders.findIndex((p) => p.id === id);

    if (providerIndex === -1) {
      throw new Error('Ordering provider not found');
    }

    allProviders.splice(providerIndex, 1);
    this.persistOrderingProviders(allProviders);
  }

  async validateOrderingPhysicianEmail(
    email: string,
  ): Promise<{ isValid: boolean; isAlreadyUsed: boolean }> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);

    // Check if email is already used by another provider
    // Skip checking if we're editing the current provider
    const allProviders = this.loadOrderingProviders();
    const isAlreadyUsed = allProviders.some(
      (provider) =>
        provider.orderingPhysician?.email?.toLowerCase() ===
        email.toLowerCase(),
    );

    return { isValid: isValid, isAlreadyUsed };
  }

  async getPathologyLabs(params: {
    searchText?: string;
    cursor?: string | null;
    first?: number;
  }): Promise<CursorPaginatedResponse<PathologyLabFieldsFragment>> {
    const { searchText, cursor, first = 15 } = params;

    // Filter labs based on search text
    let filteredLabs = FAKE_PATHOLOGY_LABS;
    if (searchText && searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filteredLabs = FAKE_PATHOLOGY_LABS.filter((lab) =>
        lab.name.toLowerCase().includes(searchLower),
      );
    }

    // Convert cursor to start index (cursor is the index as a string)
    const startIndex = cursor ? parseInt(cursor, 10) : 0;
    const endIndex = startIndex + first;
    const paginatedLabs = filteredLabs.slice(startIndex, endIndex);

    // Generate next cursor (index of the next item, or null if no more items)
    const hasNextPage = endIndex < filteredLabs.length;
    const nextCursor = hasNextPage ? String(endIndex) : null;

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    return {
      items: paginatedLabs,
      nextCursor,
      hasNextPage,
    };
  }

  async updateOrderPathologyLab(
    orderId: string,
    pathologyLabSalesforceId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    await new Promise((resolve) => setTimeout(resolve, 300));

    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === orderId);

    if (orderIndex === -1) {
      throw new Error('Order not found');
    }

    const selectedLab = FAKE_PATHOLOGY_LABS.find(
      (lab) => lab.id === pathologyLabSalesforceId,
    );

    if (!selectedLab) {
      throw new Error('Pathology Lab not found');
    }

    const updatedOrder = {
      ...orders[orderIndex],
      pathologyLab: selectedLab,
      updatedAt: new Date().toISOString(),
    };

    orders[orderIndex] = updatedOrder;
    this.persistOrders(orders);

    return updatedOrder;
  }

  async deleteFile(fileId: string): Promise<void> {
    // Find and remove the file from all orders
    const orders = this.loadOrders();
    let fileRemoved = false;

    for (let i = 0; i < orders.length; i++) {
      const order = orders[i];
      if (order.uploadedFiles?.edges) {
        const initialLength = order.uploadedFiles.edges.length;
        order.uploadedFiles.edges = order.uploadedFiles.edges.filter(
          (edge) => edge?.node?.id !== fileId,
        );
        if (order.uploadedFiles.edges.length < initialLength) {
          fileRemoved = true;
          orders[i] = order;
        }
      }
    }

    if (fileRemoved) {
      this.persistOrders(orders);
    }
  }

  async getFileDownloadUrl(_fileId: string): Promise<string> {
    // For fake implementation, return a mock presigned URL.
    return Promise.resolve('https://example.com/download/file.pdf');
  }

  async updateOrderStatusQCApproved(params: {
    orderId: string;
    note?: string | null;
    pathologyFaxAction?: PathologyFaxAction | null;
    uploadedFileIds?: string[] | null;
  }): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const order = orders.find((o) => o.id === params.orderId);
    if (!order) {
      throw new Error(`Order with ID ${params.orderId} not found`);
    }

    // QC Approved is no longer a resting status. Match GraphQL:
    // Ancillary → AI_PROCESSING; Clinic → PATHOLOGY_FAX_PENDING or LAB_PROCESSING.
    let nextStatus: OrderStatus;
    if (this.getWorkflowType() === WorkflowType.ANCILLARY_LAB) {
      nextStatus = OrderStatus.AiProcessing;
    } else if (
      params.pathologyFaxAction === PathologyFaxAction.FaxPathologyFiles
    ) {
      nextStatus = OrderStatus.PathologyFaxPending;
    } else {
      nextStatus = OrderStatus.LabProcessing;
    }

    const updatedOrder = {
      ...order,
      status: nextStatus,
      statusUpdatedAt: new Date().toISOString(),
    };
    const index = orders.findIndex((o) => o.id === params.orderId);
    orders[index] = updatedOrder;
    this.persistOrders(orders);
    return updatedOrder;
  }

  async updateOrderStatus(
    orderId: string,
    status: OrderStatus,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const order = orders.find((o) => o.id === orderId);
    if (!order) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    // Update order status
    const updatedOrder = {
      ...order,
      status,
      statusUpdatedAt: new Date().toISOString(),
    };
    const index = orders.findIndex((o) => o.id === orderId);
    orders[index] = updatedOrder;
    this.persistOrders(orders);
    return updatedOrder;
  }

  async faxTestReportToPathologyLab(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const order = orders.find((o) => o.id === orderId);
    if (!order) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    // Update order status to ReportFaxed
    const updatedOrder = {
      ...order,
      status: OrderStatus.ReportFaxed,
      statusUpdatedAt: new Date().toISOString(),
    };
    const index = orders.findIndex((o) => o.id === orderId);
    orders[index] = updatedOrder;
    this.persistOrders(orders);
    return updatedOrder;
  }

  async faxTestReportToOrderingProvider(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const order = orders.find((o) => o.id === orderId);
    if (!order) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    // Update order status to ReportFaxPending
    const updatedOrder = {
      ...order,
      status: OrderStatus.ReportFaxPending,
      statusUpdatedAt: new Date().toISOString(),
    };
    const index = orders.findIndex((o) => o.id === orderId);
    orders[index] = updatedOrder;
    this.persistOrders(orders);
    return updatedOrder;
  }

  async shareTrr(
    orderId: string,
    _emails: string[],
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const order = orders.find((o) => o.id === orderId);
    if (!order) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    // Fake implementation: no state change; report share link is sent to emails externally
    return order;
  }

  async getOrderForReviseReport(
    orderId: string,
  ): Promise<LdtOrderForReviseReportFieldsFragment | null> {
    return this.getOrderById(orderId, { view: LdtOrderDetailView.Default });
  }

  async reviseReport(
    input: ReviseReportInput,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === input.orderId);
    const order = orders[orderIndex];
    if (!order) {
      throw new Error(`Order with ID ${input.orderId} not found`);
    }

    const biopsyData = input.prostateBiopsyData;
    const postRpData = input.prostatePostRpData;
    const metastaticData = input.metastaticProstateData;

    const allProviders = this.loadOrderingProviders();
    const resolvedProvider =
      allProviders.find((p) => p.id === input.orderingProviderId) ??
      order.orderingProvider;

    const updatedOrder = {
      ...order,
      orderingProvider: resolvedProvider,
      patient: {
        ...order.patient,
        firstNamePhi: input.patientFirstNamePhi,
        lastNamePhi: input.patientLastNamePhi,
        dateOfBirthPhi: input.patientDateOfBirthPhi,
        ethnicity: input.patientEthnicity ?? order.patient?.ethnicity ?? null,
      },
      specimenId: input.specimenId ?? order.specimenId,
      ...(biopsyData && {
        primaryGleasonScore: biopsyData.primaryGleasonScore,
        secondaryGleasonScore: biopsyData.secondaryGleasonScore,
        psa: biopsyData.psa,
        tStage: biopsyData.tStage,
        nccnRiskCategory: biopsyData.nccnRiskCategory,
        totalCores: biopsyData.totalCores,
        positiveCores: biopsyData.positiveCores,
      }),
      ...(postRpData && {
        primaryGleasonScore: postRpData.primaryGleasonScore,
        secondaryGleasonScore: postRpData.secondaryGleasonScore,
        psa: postRpData.psa,
        tStage: postRpData.tStage,
        surgicalMargin: postRpData.surgicalMargin,
      }),
      ...(metastaticData && {
        psa: metastaticData.psa,
        tStage: metastaticData.tStage,
      }),
    } as unknown as LdtOrderDetailFieldsFragment;

    orders[orderIndex] = updatedOrder;
    this.persistOrders(orders);
    return updatedOrder;
  }
  async refaxPathologyFiles(params: {
    orderId: string;
    pathologyFaxAction: PathologyFaxAction;
    note?: string | null;
    uploadedFileIds?: string[] | null;
  }): Promise<LdtOrderDetailFieldsFragment> {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === params.orderId);
    const order = orders[orderIndex];
    if (!order) {
      throw new Error('Order not found');
    }
    if (
      params.pathologyFaxAction === PathologyFaxAction.FaxPathologyFiles &&
      !order.pathologyLab?.faxNumber
    ) {
      throw new Error('Sorry, pathology files cannot be faxed at this time.');
    }

    const nextStatus =
      params.pathologyFaxAction === PathologyFaxAction.FaxPathologyFiles
        ? OrderStatus.PathologyFaxPending
        : OrderStatus.LabProcessing;

    orders[orderIndex] = {
      ...order,
      status: nextStatus,
    } as unknown as LdtOrderDetailFieldsFragment;

    this.persistOrders(orders);

    return orders[orderIndex];
  }

  async updateBillingHold(
    orderId: string,
    hasBillingHold: boolean,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === orderId);
    if (orderIndex === -1) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    orders[orderIndex].hasBillingHold = hasBillingHold;
    this.persistOrders(orders);
    return orders[orderIndex];
  }

  async getMostRecentFax(
    _orderId: string,
    _faxType: FaxType,
  ): Promise<FaxFieldsFragment | null> {
    const orders = this.loadOrders();
    const order = orders.find((o) => o.id === _orderId);
    if (!order) {
      throw new Error(`Order with ID ${_orderId} not found`);
    }

    return {
      status: ArteraFaxStatus.Success,
      selectedFiles: order.uploadedFiles?.edges.map((edge) => edge?.node) ?? [],
    } as unknown as FaxFieldsFragment;
  }

  async rejectReport(
    orderId: string,
    _reason: string,
    newStatus: OrderStatus | null,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === orderId);
    if (orderIndex === -1) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    // Fake implementation: update order status if newStatus provided
    if (newStatus) {
      const allowedStatuses = [
        OrderStatus.LabProcessing,
        OrderStatus.QualityControlPending,
      ];
      if (!allowedStatuses.includes(newStatus)) {
        throw new Error(`Invalid new status: ${newStatus}`);
      }

      orders[orderIndex].status = newStatus;
    } else {
      orders[orderIndex].status = OrderStatus.ReportReady;
    }

    this.persistOrders(orders);

    return orders[orderIndex];
  }

  async qcApproveSlideImage(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === orderId);
    if (orderIndex === -1) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    orders[orderIndex].status = OrderStatus.AiProcessing;
    this.persistOrders(orders);

    return orders[orderIndex];
  }

  async qcRejectSlideImage(
    orderId: string,
    _reason: string,
  ): Promise<LdtOrderDetailFieldsFragment> {
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === orderId);
    if (orderIndex === -1) {
      throw new Error(`Order with ID ${orderId} not found`);
    }
    orders[orderIndex].status = OrderStatus.SlideImageQualityControlFailed;
    this.persistOrders(orders);

    return orders[orderIndex];
  }
}

/** PSA + T-stage + procedure date — fake Tempus metastatic completeness check. */
function isMetastaticTempusClinicalDataComplete(
  order: LdtOrderDetailFieldsFragment,
): boolean {
  const hasPsa = order.psa !== null && order.psa !== undefined;
  const ts = order.tStage as unknown;
  const hasTStage =
    ts !== null &&
    ts !== undefined &&
    ts !== '' &&
    !(typeof ts === 'string' && ts.trim() === '');
  const dateStr = order.labProcedureDatePhi;
  const hasProcedureDate =
    dateStr !== null && dateStr !== undefined && String(dateStr).trim() !== '';
  return hasPsa && hasTStage && hasProcedureDate;
}
